import { MONTHS } from '@/common/constants';
import { REAGION_SOATO } from '@/common/constants/regions';
import { ICottonData, IDataRepublic } from '../models/IMonitoring';

const HEADER_BG = 'FFF3F4F6';
const MONTH_BG = 'FFF3F4F6';
const ROW_ODD_BG = 'FFF9FAFB';
const ROW_EVEN_BG = 'FFFFFFFF';
const BORDER_COLOR = 'FFD1D5DB';

const MONTHS_ORDER = [
    'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
    'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
];

const border = () => ({
    top: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    left: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    bottom: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
    right: { style: 'thin' as const, color: { argb: BORDER_COLOR } },
});

export async function exportMonitoringToExcel(
    dataRegions: IDataRepublic[],
    cottonData: ICottonData[],
    plantId: string,
    t: (key: string) => string,
    filename = 'monitoring-respublika',
) {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Monitoring');

    // Viloyatlarni REAGION_SOATO tartibida joylashtirish
    const regions = REAGION_SOATO
        .map((r) => dataRegions.find((d) => d.region_soato === r.soato))
        .filter(Boolean) as IDataRepublic[];

    const totalCols = 1 + regions.length;

    ws.columns = [
        { width: 36 },
        ...regions.map(() => ({ width: 18 })),
    ];

    // ─── HEADER ROW ────────────────────────────────────────────────────────────
    const headerValues = ['', ...regions.map((r) => r.name_uz)];
    const hRow = ws.addRow(headerValues);
    hRow.height = 40;
    hRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true, size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
        cell.border = border();
        if (colNumber > 1) {
            cell.font = { bold: true, size: 10, color: { argb: 'FF1E40AF' } };
        }
    });

    // ─── MONITORED ROW ─────────────────────────────────────────────────────────
    const monitoredValues: (string | number)[] = [
        'Monitoring qilinadigan umumiy yer rejasi (ga)',
        ...regions.map((region) => {
            const data = cottonData?.find((el) => el.region_soato === region.region_soato);
            if (plantId === '1') return Number(data?.grain_plan || 0);
            if (plantId === '2') return Number(data?.cotton_plan || 0);
            return '-';
        }),
    ];
    const monRow = ws.addRow(monitoredValues);
    monRow.height = 22;
    monRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ROW_EVEN_BG } };
        cell.border = border();
        if (colNumber === 1) {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
            cell.font = { size: 10 };
        } else {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.font = { size: 10, color: { argb: 'FF1E40AF' } };
            if (typeof cell.value === 'number') cell.numFmt = '#,##0.00';
        }
    });

    // ─── Oylar bo'yicha yig'ib chiqish ─────────────────────────────────────────
    const allMonths = Array.from(
        new Set(
            regions
                .flatMap((region) => region?.inspected_area || [])
                .filter(Boolean)
                .map((item) => item.month),
        ),
    ).sort((a, b) => MONTHS_ORDER.indexOf(a) - MONTHS_ORDER.indexOf(b));

    const getActionsByMonth = (month: string) => {
        const actions = new Map<string, { action_name: string; planting_type?: string }>();
        regions.forEach((region) => {
            (region?.inspected_area || [])
                .filter((item) => item?.month === month)
                .forEach((item) => {
                    if (item.action_name) {
                        const key = `${item.action_name}-${item.planting_type || ''}`;
                        actions.set(key, { action_name: item.action_name, planting_type: item.planting_type as string });
                    }
                });
        });
        return Array.from(actions.values());
    };

    const getValue = (region: IDataRepublic, month: string, actionName: string, plantingType?: string) => {
        const action = region?.inspected_area?.find(
            (item) => item?.month === month && item?.action_name === actionName && (item?.planting_type || '') === (plantingType || ''),
        );
        return Number(action?.inspected_area || 0);
    };

    let dataRowIdx = 0;

    allMonths.forEach((month) => {
        // Oy sarlavha qatori
        const monthLabel = MONTHS.find((m) => m.value === month)?.label || month;
        const monthValues: (string | number)[] = [monthLabel, ...regions.map(() => '')];
        const monthRow = ws.addRow(monthValues);
        monthRow.height = 28;
        for (let c = 1; c <= totalCols; c++) {
            const cell = ws.getCell(monthRow.number, c);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: MONTH_BG } };
            cell.border = border();
            cell.font = { bold: true, size: 10 };
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
        }
        // Birinchi katak uchun merge
        ws.mergeCells(monthRow.number, 1, monthRow.number, totalCols);

        // Action qatorlari
        const actions = getActionsByMonth(month);
        actions.forEach((action, actionIdx) => {
            const rowBg = dataRowIdx % 2 === 0 ? ROW_EVEN_BG : ROW_ODD_BG;
            const { action_name: actionName, planting_type: plantingType } = action;
            const actionLabel = plantingType
                ? `${actionIdx + 1}. ${actionName} (${t(plantingType)})`
                : `${actionIdx + 1}. ${actionName}`;

            const values: (string | number)[] = [
                actionLabel,
                ...regions.map((region) => getValue(region, month, actionName, plantingType)),
            ];
            const row = ws.addRow(values);
            row.height = 22;
            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
                cell.border = border();
                if (colNumber === 1) {
                    cell.alignment = { horizontal: 'left', vertical: 'middle' };
                    cell.font = { size: 10 };
                } else {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.font = { size: 10, color: { argb: 'FF1E40AF' } };
                    cell.numFmt = '#,##0.00';
                }
            });
            dataRowIdx++;
        });
    });

    // ─── FREEZE PANES ──────────────────────────────────────────────────────────
    ws.views = [{ state: 'frozen', xSplit: 1, ySplit: 1 }];

    // ─── DOWNLOAD ──────────────────────────────────────────────────────────────
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
}
